import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { oesApi } from '../services/api';
import Layout from '../components/layout/Layout';
import CustomSelect from '../components/common/CustomSelect';
import { useT } from '../providers/I18nProvider';

// NIST CSF function colors for left border and accent
const NIST_CSF_FUNCTION_COLORS = {
  Govern: '#6B4E9B',      // Purple
  Identify: '#E87722',     // Orange
  Protect: '#0066B3',       // Blue
  Detect: '#F9C642',        // Yellow/Gold
  Respond: '#E31837',       // Red
  Recover: '#00A651',       // Green
};

function getFunctionBorderColor(domain) {
  return NIST_CSF_FUNCTION_COLORS[domain] || '#64748b'; // Default to slate if unknown
}

function getFunctionAccentColor(domain) {
  return NIST_CSF_FUNCTION_COLORS[domain] || '#64748b'; // Default to slate if unknown
}

const STEPS = 3;
const ITEMS_PER_STEP = 17;

export default function OESQuestionnaire() {
  const t = useT();
  const navigate = useNavigate();
  const { oesId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [frameworkName, setFrameworkName] = useState('');
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const COMPLIANCE_OPTIONS = [
    { value: 'non_compliant', label: t('posture.nonCompliant') },
    { value: 'partially_compliant', label: t('posture.partiallyCompliant') },
    { value: 'fully_compliant', label: t('posture.fullyCompliant') },
    { value: 'not_applicable', label: t('posture.notApplicable') },
  ];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await oesApi.getQuestionnaire(Number(oesId));
        if (cancelled) return;
        setFrameworkName(res.data.framework_name || '');
        setItems(res.data.items || []);
        if (res.data.existing && typeof res.data.existing === 'object') {
          setResponses(res.data.existing);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load questionnaire');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [oesId]);

  const start = step * ITEMS_PER_STEP;
  const chunk = items.slice(start, start + ITEMS_PER_STEP);
  const totalSteps = Math.ceil(items.length / ITEMS_PER_STEP) || 1;
  const isLastStep = step >= totalSteps - 1;
  const progressPct = totalSteps > 0 ? ((step + 1) / totalSteps) * 100 : 0;

  const setResponse = (ref, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [ref]: { ...(prev[ref] || {}), [field]: value },
    }));
  };

  const handleNext = () => {
    if (isLastStep) {
      handleSubmit();
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setStep((s) => Math.max(0, s - 1));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const allRefs = items.map((i) => i.ref);
      const responsesPayload = {};
      for (const ref of allRefs) {
        const r = responses[ref];
        // Only include items that have been assessed (skip unassessed items)
        if (r?.compliance) {
          responsesPayload[ref] = {
            compliance: r.compliance,
            explanation: r.explanation || null,
            evidence: r.evidence || null,
          };
        }
      }
      await oesApi.submitQuestionnaire(Number(oesId), { responses: responsesPayload });
      navigate(`/oes/${oesId}/questionnaire-result`, { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-12 text-center text-slate-600">{t('posture.loadingQuestionnaire')}</div>
      </Layout>
    );
  }

  if (error && !items.length) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            {t('posture.goBack')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-eucnet-navy">{t('assessment.postureAssessment')}</h1>
          <p className="text-slate-600 mt-1">{frameworkName}</p>
          <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-eucnet-sky transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {t('posture.step', { current: step + 1, total: totalSteps })}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        <div className="space-y-6">
          {chunk.map((item) => (
            <div
              key={item.id}
              className="rounded-lg p-4 bg-white shadow-sm border border-slate-200"
            >
              <div className="text-sm font-semibold text-eucnet-navy mb-2 flex items-center gap-2">
                <span
                  className="inline-block w-1 h-4 rounded-full"
                  style={{ backgroundColor: getFunctionAccentColor(item.domain) }}
                />
                <span>{item.domain} / {item.subdomain}</span>
              </div>
              <p className="text-slate-700 text-sm mb-4">{item.description}</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('posture.compliance')}</label>
                  <CustomSelect
                    value={responses[item.ref]?.compliance || ''}
                    onChange={(e) => setResponse(item.ref, 'compliance', e.target.value)}
                    options={COMPLIANCE_OPTIONS}
                    placeholder="Select…"
                    searchable={false}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('posture.explanation')}</label>
                  <input
                    type="text"
                    value={responses[item.ref]?.explanation || ''}
                    onChange={(e) => setResponse(item.ref, 'explanation', e.target.value)}
                    placeholder="Optional explanation or context"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-eucnet-sky focus:border-transparent"
                  />
                </div>
                {item.evidence_prompt && responses[item.ref]?.compliance === 'fully_compliant' && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <label className="block text-sm font-medium text-amber-800 mb-1">
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        {item.evidence_prompt}
                      </span>
                    </label>
                    <input
                      type="text"
                      value={responses[item.ref]?.evidence || ''}
                      onChange={(e) => setResponse(item.ref, 'evidence', e.target.value)}
                      placeholder={t('posture.evidencePrompt')}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.back')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-eucnet-sky text-white hover:bg-eucnet-navy disabled:opacity-50"
          >
            {submitting ? t('common.submitting') : isLastStep ? t('common.submit') : t('common.next')}
          </button>
        </div>
      </div>
    </Layout>
  );
}
